import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useVault } from "@/context/VaultContext";
import { safetyNumber, sha256Hex } from "@/lib/identity";
import { nameOf } from "@/lib/selectors";
import { OrbitalMark } from "@/components/aph/OrbitalLock";

const COPY = {
  connected: "Signed, end-to-end encrypted traffic verified from every peer. The relay only sees sealed ciphertext and an ephemeral routing tag.",
  pending: "Keys exchanged locally. Waiting for a signed handshake from the other side — messages are still sealed and held on the relay until delivered.",
  compromised: "A payload claiming to be from this contact failed signature verification. Verify the safety number in person before trusting this channel.",
};

export function LockDialog({ open, onOpenChange, session, contacts }) {
  const { profile } = useVault();
  const [safety, setSafety] = useState([]);
  const [fp, setFp] = useState("");

  useEffect(() => {
    if (!open) return;
    const codes = session.kind === "direct" ? [profile.code, session.recipient_code] : session.members;
    safetyNumber(codes).then(setSafety);
    const secret = session.kind === "direct" ? contacts[session.recipient_code]?.shared_secret : null;
    if (secret) sha256Hex(secret).then((h) => setFp(h.slice(0, 32).match(/.{4}/g).join(" ")));
  }, [open, session, profile.code, contacts]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="lock-dialog" className="bg-nebula border-rim text-ash sm:max-w-md rounded-secure">
        <DialogHeader>
          <DialogTitle className="font-branding flex items-center gap-3"><OrbitalMark className="w-8 h-8" status={session.lock} /> Orbital Lock · <span data-testid="lock-dialog-status" className={session.lock === "compromised" ? "text-flare" : session.lock === "connected" ? "text-stellar" : "text-ash/60"}>{session.lock}</span></DialogTitle>
          <DialogDescription className="text-ash/55">{COPY[session.lock]}</DialogDescription>
        </DialogHeader>
        <div>
          <p className="text-[11px] uppercase tracking-[0.16em] text-ash/40 font-branding mb-2">Safety number</p>
          <div className="grid grid-cols-4 gap-2 font-mono text-sm bg-void border border-glass rounded-secure p-3" data-testid="lock-safety-number">
            {safety.map((s, i) => <span key={i} className="text-center text-ash/85">{s}</span>)}
          </div>
          <p className="text-xs text-ash/40 mt-2">Compare in person. Matching numbers mean no one is in the middle.</p>
        </div>
        {fp && (
          <div>
            <p className="text-[11px] uppercase tracking-[0.16em] text-ash/40 font-branding mb-1.5">Session key fingerprint (X25519)</p>
            <p className="font-mono text-xs text-stellar/80" data-testid="lock-session-fingerprint">{fp}</p>
          </div>
        )}
        {session.kind === "group" && (
          <div className="space-y-1.5" data-testid="lock-member-list">
            {session.peers.map((c) => (
              <div key={c} className="flex items-center justify-between text-sm bg-void border border-glass rounded-secure px-3 py-2">
                <span className="font-branding uppercase tracking-wider">{nameOf(contacts[c])}</span>
                <OrbitalMark className="w-5 h-5" status={contacts[c]?.lock_status || "pending"} />
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
